import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Heart, UserPlus, LogOut, Check, X, Archive, AlertTriangle, KeyRound, Wallet, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

export default function ParInnstillinger() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [couple, setCouple] = useState(null);
  const [loading, setLoading] = useState(true);
  // invite-skjema
  const [showInvite, setShowInvite] = useState(false);
  const [partnerUsername, setPartnerUsername] = useState("");
  const [coupleName, setCoupleName] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Dual-email aktivering
  const [actSide, setActSide] = useState("a");
  const [actUsername, setActUsername] = useState("");
  const [actPassword, setActPassword] = useState("");
  const [activating, setActivating] = useState(false);

  // Wallet
  const [wallet, setWallet] = useState(null);
  const [wAmount, setWAmount] = useState(0);
  const [wDirection, setWDirection] = useState("to_couple");
  const [wBusy, setWBusy] = useState(false);

  // Partner-detaljer (kjønn + alder pr partner)
  const [pdForm, setPdForm] = useState({
    partner_a_gender: "",
    partner_a_age: "",
    partner_b_gender: "",
    partner_b_age: "",
  });
  const [pdBusy, setPdBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/couple");
      setCouple(data);
      if (data?.id && data.status === "active") {
        try {
          const { data: w } = await api.get(`/couples/${data.id}/wallet`);
          setWallet(w);
        } catch { /* wallet kan feile før paret er aktivt */ }
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Pre-fyll partner-detaljer når couple lastes
  useEffect(() => {
    if (couple) {
      setPdForm({
        partner_a_gender: couple.partner_a_gender || "",
        partner_a_age:    couple.partner_a_age || "",
        partner_b_gender: couple.partner_b_gender || "",
        partner_b_age:    couple.partner_b_age || "",
      });
    }
  }, [couple?.id, couple?.partner_a_gender, couple?.partner_b_gender, couple?.partner_a_age, couple?.partner_b_age]);

  const savePartnerDetails = async (e) => {
    e?.preventDefault?.();
    if (!pdForm.partner_a_gender || !pdForm.partner_b_gender) {
      toast.error(t("couple.gender_required", "Velg kjønn for begge partnere."));
      return;
    }
    const ageA = parseInt(pdForm.partner_a_age, 10);
    const ageB = parseInt(pdForm.partner_b_age, 10);
    if (!ageA || ageA < 18 || ageA > 120 || !ageB || ageB < 18 || ageB > 120) {
      toast.error(t("couple.age_required", "Begge partnere må være 18 år eller eldre."));
      return;
    }
    setPdBusy(true);
    try {
      const { data } = await api.put(`/couples/${couple.id}`, {
        partner_a_gender: pdForm.partner_a_gender,
        partner_a_age: ageA,
        partner_b_gender: pdForm.partner_b_gender,
        partner_b_age: ageB,
      });
      setCouple(data);
      toast.success(t("couple.partner_details_saved", "Partner-detaljer lagret."));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("couple.update_failed", "Kunne ikke oppdatere."));
    } finally {
      setPdBusy(false);
    }
  };

  const activateMe = async (e) => {
    e?.preventDefault?.();
    if (!actUsername.trim() || !actPassword) {
      toast.error(t("couple.fill_username_password"));
      return;
    }
    setActivating(true);
    try {
      await api.post(`/couples/${couple.id}/activate-partner`, {
        side: actSide,
        username: actUsername.trim(),
        password: actPassword,
      });
      toast.success(t("couple.activation_success", { username: actUsername.trim() }));
      await refresh?.();
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("couple.activation_failed"));
    } finally {
      setActivating(false);
    }
  };

  const transferWallet = async (e) => {
    e?.preventDefault?.();
    const amount = parseInt(wAmount, 10);
    if (!amount || amount <= 0) {
      toast.error(t("couple.wallet_amount_invalid"));
      return;
    }
    setWBusy(true);
    try {
      const { data } = await api.post(`/couples/${couple.id}/wallet/transfer`, {
        direction: wDirection,
        amount,
      });
      setWallet({ couple_id: couple.id, couple_balance: data.couple_balance, your_balance: data.your_balance });
      setWAmount(0);
      toast.success(t("couple.transferred"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("couple.wallet_transfer_failed"));
    } finally {
      setWBusy(false);
    }
  };

  const invite = async (e) => {
    e?.preventDefault?.();
    if (!partnerUsername.trim() || !coupleName.trim()) {
      toast.error(t("couple.invite_filled_warning"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/couples/invite", {
        partner_username: partnerUsername.trim(),
        couple_name: coupleName.trim(),
        message: inviteMessage.trim() || null,
      });
      toast.success(t("couple.invite_sent"));
      setShowInvite(false);
      setPartnerUsername(""); setCoupleName(""); setInviteMessage("");
      await load();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || t("couple.invite_failed"));
    } finally { setSubmitting(false); }
  };

  const accept = async () => {
    setSubmitting(true);
    try {
      await api.post(`/couples/${couple.id}/accept`);
      toast.success(t("couple.couple_activated"));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || t("couple.error_generic")); }
    finally { setSubmitting(false); }
  };

  const decline = async () => {
    if (!window.confirm(t("couple.decline_confirm"))) return;
    setSubmitting(true);
    try {
      await api.post(`/couples/${couple.id}/decline`);
      toast.success(t("couple.invite_declined"));
      setCouple(null);
    } catch (e) { toast.error(e.response?.data?.detail || t("couple.error_generic")); }
    finally { setSubmitting(false); }
  };

  const leave = async () => {
    if (!window.confirm(t("couple.leave_confirm"))) return;
    setSubmitting(true);
    try {
      await api.post(`/couples/${couple.id}/leave`);
      toast.success(t("couple.left_couple"));
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || t("couple.error_generic")); }
    finally { setSubmitting(false); }
  };

  const isInviter = couple && user && couple.partner_a_user_id === user.id;
  const isInvitee = couple && user && couple.partner_b_user_id === user.id;
  const myUsername = user?.username;
  const partner = couple ? (isInviter ? couple.partner_b : couple.partner_a) : null;

  return (
    <Layout>
      <div className="max-w-3xl">
        <div className="flex items-center gap-3 mb-2">
          <Heart className="h-6 w-6 text-[var(--magenta)]" />
          <div className="font-meta">{t("couple.section_label")}</div>
        </div>
        <h1 className="font-fraunces italic text-4xl text-[var(--cream)] mb-3">{t("couple.your_couple_link")}</h1>
        <p className="text-sm text-[var(--cream-soft)] mb-8 max-w-xl leading-relaxed">
          {t("couple.intro")}
        </p>

        {loading && <div className="font-meta text-[var(--cream-soft)]">{t("common.loading")}</div>}

        {!loading && !couple && (
          <div className="panel-tight p-6">
            {!showInvite ? (
              <button onClick={() => setShowInvite(true)} className="btn-magenta inline-flex items-center" data-testid="par-invite-btn">
                <UserPlus className="h-4 w-4 mr-2" /> {t("couple.invite_partner")}
              </button>
            ) : (
              <form onSubmit={invite} className="space-y-4">
                <div>
                  <label className="font-meta text-xs text-[var(--cream-soft)] block mb-2">{t("couple.partner_username")}</label>
                  <input
                    value={partnerUsername}
                    onChange={(e) => setPartnerUsername(e.target.value)}
                    placeholder={t("couple.partner_username_placeholder")}
                    className="input-editorial w-full"
                    data-testid="par-invite-username"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="font-meta text-xs text-[var(--cream-soft)] block mb-2">{t("couple.couple_name_label")}</label>
                  <input
                    value={coupleName}
                    onChange={(e) => setCoupleName(e.target.value)}
                    placeholder={myUsername ? t("couple.couple_name_placeholder_with_user", { user: myUsername }) : t("couple.couple_name_placeholder_default")}
                    className="input-editorial w-full"
                    data-testid="par-invite-name"
                  />
                </div>
                <div>
                  <label className="font-meta text-xs text-[var(--cream-soft)] block mb-2">{t("couple.optional_message")}</label>
                  <textarea
                    value={inviteMessage}
                    onChange={(e) => setInviteMessage(e.target.value)}
                    rows={3}
                    className="input-editorial w-full"
                    data-testid="par-invite-msg"
                  />
                </div>
                <div className="flex gap-3">
                  <button type="submit" disabled={submitting} className="btn-magenta" data-testid="par-invite-submit">
                    {submitting ? t("couple.sending") : t("couple.send_invitation")}
                  </button>
                  <button type="button" onClick={() => setShowInvite(false)} className="btn-ghost">{t("common.cancel")}</button>
                </div>
              </form>
            )}
          </div>
        )}

        {!loading && couple && couple.status === "pending" && (
          <div className="panel-tight p-6 border-l-2 border-amber-500">
            <div className="flex items-center gap-2 mb-3 font-meta text-amber-300">
              <AlertTriangle className="h-3.5 w-3.5" /> {t("couple.waiting_approval")}
            </div>
            <div className="font-fraunces italic text-2xl text-[var(--cream)] mb-2">{couple.name}</div>
            {isInviter && (
              <p
                className="text-sm text-[var(--cream-soft)] mb-4"
                dangerouslySetInnerHTML={{
                  __html: t("couple.you_invited_partner", { partner: couple.partner_b?.username }),
                }}
              />
            )}
            {isInvitee && (
              <>
                <p
                  className="text-sm text-[var(--cream-soft)] mb-4"
                  dangerouslySetInnerHTML={{
                    __html: t("couple.partner_invited_you", {
                      partner: couple.partner_a?.username,
                      name: couple.name,
                    }),
                  }}
                />
                {couple.invite_message && (
                  <blockquote className="border-l-2 border-[var(--magenta)] pl-4 py-2 text-sm text-[var(--cream)] italic mb-4">
                    «{couple.invite_message}»
                  </blockquote>
                )}
                <div className="flex gap-3">
                  <button onClick={accept} disabled={submitting} className="btn-magenta inline-flex items-center" data-testid="par-accept-btn">
                    <Check className="h-4 w-4 mr-2" /> {t("couple.accept")}
                  </button>
                  <button onClick={decline} disabled={submitting} className="btn-ghost inline-flex items-center" data-testid="par-decline-btn">
                    <X className="h-4 w-4 mr-2" /> {t("couple.decline")}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {!loading && couple && couple.status === "active" && (
          <div className="panel-tight p-6">
            <div className="flex items-center gap-2 mb-4 font-meta text-emerald-400">
              <Check className="h-3.5 w-3.5" /> {t("couple.active_couple_profile")}
            </div>
            <div className="font-fraunces italic text-3xl text-[var(--cream)] mb-1">{couple.name}</div>
            <Link to={`/par/${couple.handle}`} className="text-[var(--magenta)] hover:underline text-sm">
              norskerotikk.no/par/{couple.handle}
            </Link>
            <div className="text-sm text-[var(--cream-soft)] mt-3 mb-6">
              {couple.registration_mode === "dual_email" ? (
                <>
                  <div>{t("couple.email_a")}: <strong className="text-[var(--cream)]">{couple.partner_a_email}</strong> {couple.partner_a_user_id ? <span className="text-emerald-400">{t("couple.activated_as", { username: couple.partner_a?.username })}</span> : <span className="text-amber-400">{t("couple.dormant")}</span>}</div>
                  <div className="mt-1">{t("couple.email_b")}: <strong className="text-[var(--cream)]">{couple.partner_b_email}</strong> {couple.partner_b_user_id ? <span className="text-emerald-400">{t("couple.activated_as", { username: couple.partner_b?.username })}</span> : <span className="text-amber-400">{t("couple.dormant")}</span>}</div>
                </>
              ) : (
                <span dangerouslySetInnerHTML={{ __html: t("couple.paired_with", { partner: partner?.username }) }} />
              )}
            </div>

            {couple.bio && <p className="text-sm text-[var(--cream)] mb-6 leading-relaxed">{couple.bio}</p>}

            {/* Aktiver sovende singel-profil */}
            {user?.role === "couple" && (!couple.partner_a_user_id || !couple.partner_b_user_id) && (
              <form onSubmit={activateMe} className="border-t border-white/10 pt-6 mt-2 space-y-4" data-testid="par-activate-form">
                <div className="flex items-center gap-2 font-meta text-[var(--magenta)] mb-1">
                  <KeyRound className="h-3.5 w-3.5" /> {t("couple.activation_title")}
                </div>
                <p className="text-xs text-[var(--cream-soft)] leading-relaxed">
                  {t("couple.activation_intro")}
                </p>
                <div>
                  <label className="font-meta text-xs text-[var(--cream-soft)] block mb-2">{t("couple.which_side")}</label>
                  <div className="flex gap-2">
                    {!couple.partner_a_user_id && (
                      <button type="button" onClick={() => setActSide("a")}
                              className={`px-4 py-2 text-xs font-meta border ${actSide === "a" ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/15 text-[var(--cream-soft)]"}`}
                              data-testid="par-activate-side-a">
                        Partner A · {couple.partner_a_email}
                      </button>
                    )}
                    {!couple.partner_b_user_id && (
                      <button type="button" onClick={() => setActSide("b")}
                              className={`px-4 py-2 text-xs font-meta border ${actSide === "b" ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/15 text-[var(--cream-soft)]"}`}
                              data-testid="par-activate-side-b">
                        Partner B · {couple.partner_b_email}
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label className="font-meta text-xs text-[var(--cream-soft)] block mb-2">{t("couple.your_new_username")}</label>
                  <input value={actUsername} onChange={(e) => setActUsername(e.target.value)}
                         className="input-editorial" minLength={3} maxLength={24}
                         placeholder="Lars_85" data-testid="par-activate-username" />
                </div>
                <div>
                  <label className="font-meta text-xs text-[var(--cream-soft)] block mb-2">{t("couple.your_new_password")}</label>
                  <input type="password" value={actPassword} onChange={(e) => setActPassword(e.target.value)}
                         className="input-editorial" minLength={6}
                         data-testid="par-activate-password" />
                  <p className="font-manrope text-[10px] text-[var(--cream-faint)] mt-2 uppercase tracking-[0.15em]">
                    {t("couple.password_hint")}
                  </p>
                </div>
                <button type="submit" disabled={activating} className="btn-magenta" data-testid="par-activate-submit">
                  {activating ? t("couple.activating") : t("couple.activate_btn")}
                </button>
              </form>
            )}

            {/* Partner-detaljer (kjønn + alder per partner) */}
            {couple.status === "active" && (
              <form onSubmit={savePartnerDetails} className="border-t border-white/10 pt-6 mt-6 space-y-4" data-testid="par-details-form">
                <div className="flex items-center gap-2 font-meta text-[var(--magenta)] mb-1">
                  <Heart className="h-3.5 w-3.5" /> {t("couple.partner_details_title", "Partner-detaljer")}
                </div>
                <p className="text-xs text-[var(--cream-soft)] leading-relaxed">
                  {t("couple.partner_details_intro", "Velg kjønn og alder for hver partner. Dette brukes til riktig par-symbol (♂♀ / ♂♂ / ♀♀) og søkefiltre.")}
                </p>
                {!couple.partner_a_gender || !couple.partner_b_gender ? (
                  <div className="bg-amber-500/10 border-l-2 border-amber-500 px-3 py-2 text-xs text-amber-200 font-manrope">
                    {t("couple.partner_details_missing_warning", "Mangler informasjon – fyll inn for å vises riktig i søk.")}
                  </div>
                ) : null}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-meta text-xs text-[var(--cream-soft)] block mb-2">
                      {t("auth.partner_a_label", "Partner A")}
                    </label>
                    <select
                      value={pdForm.partner_a_gender}
                      onChange={(e) => setPdForm((s) => ({ ...s, partner_a_gender: e.target.value }))}
                      className="input-editorial w-full"
                      data-testid="par-details-a-gender"
                    >
                      <option value="">{t("auth.choose_gender", "Velg kjønn")}</option>
                      <option value="kvinne">{t("gender.woman", "Kvinne")}</option>
                      <option value="mann">{t("gender.man", "Mann")}</option>
                      <option value="trans">{t("gender.trans", "Trans")}</option>
                      <option value="annet">{t("gender.other", "Annet")}</option>
                    </select>
                    <input
                      type="number" min={18} max={120}
                      placeholder={t("auth.age_placeholder", "Alder")}
                      value={pdForm.partner_a_age}
                      onChange={(e) => setPdForm((s) => ({ ...s, partner_a_age: e.target.value }))}
                      className="input-editorial w-full mt-2"
                      data-testid="par-details-a-age"
                    />
                  </div>
                  <div>
                    <label className="font-meta text-xs text-[var(--cream-soft)] block mb-2">
                      {t("auth.partner_b_label", "Partner B")}
                    </label>
                    <select
                      value={pdForm.partner_b_gender}
                      onChange={(e) => setPdForm((s) => ({ ...s, partner_b_gender: e.target.value }))}
                      className="input-editorial w-full"
                      data-testid="par-details-b-gender"
                    >
                      <option value="">{t("auth.choose_gender", "Velg kjønn")}</option>
                      <option value="kvinne">{t("gender.woman", "Kvinne")}</option>
                      <option value="mann">{t("gender.man", "Mann")}</option>
                      <option value="trans">{t("gender.trans", "Trans")}</option>
                      <option value="annet">{t("gender.other", "Annet")}</option>
                    </select>
                    <input
                      type="number" min={18} max={120}
                      placeholder={t("auth.age_placeholder", "Alder")}
                      value={pdForm.partner_b_age}
                      onChange={(e) => setPdForm((s) => ({ ...s, partner_b_age: e.target.value }))}
                      className="input-editorial w-full mt-2"
                      data-testid="par-details-b-age"
                    />
                  </div>
                </div>
                <button type="submit" disabled={pdBusy} className="btn-magenta" data-testid="par-details-submit">
                  {pdBusy ? t("common.saving", "Lagrer …") : t("couple.save_partner_details", "Lagre partner-detaljer")}
                </button>
              </form>
            )}

            {/* Delt lommebok */}
            {wallet && (
              <form onSubmit={transferWallet} className="border-t border-white/10 pt-6 mt-6 space-y-4" data-testid="par-wallet-form">
                <div className="flex items-center gap-2 font-meta text-[var(--cognac)] mb-1">
                  <Wallet className="h-3.5 w-3.5" /> {t("couple.shared_wallet")}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="border border-white/10 p-4">
                    <div className="font-meta text-[10px] text-[var(--cream-soft)]">{t("couple.your_private_pot")}</div>
                    <div className="font-fraunces italic text-3xl text-[var(--cream)] mt-1">{wallet.your_balance}</div>
                    <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-0.5">{t("couple.chips")}</div>
                  </div>
                  <div className="border border-[var(--magenta)]/40 p-4 bg-[var(--magenta)]/[0.04]">
                    <div className="font-meta text-[10px] text-[var(--magenta)]">{t("couple.couple_pot")}</div>
                    <div className="font-fraunces italic text-3xl text-[var(--cream)] mt-1">{wallet.couple_balance}</div>
                    <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-0.5">{t("couple.chips")}</div>
                  </div>
                </div>
                <div className="flex items-end gap-3 flex-wrap">
                  <div className="flex-1 min-w-[140px]">
                    <label className="font-meta text-xs text-[var(--cream-soft)] block mb-2">{t("couple.amount_chips")}</label>
                    <input type="number" min="1" value={wAmount} onChange={(e) => setWAmount(e.target.value)}
                           className="input-editorial" data-testid="par-wallet-amount" />
                  </div>
                  <div>
                    <label className="font-meta text-xs text-[var(--cream-soft)] block mb-2">{t("couple.direction")}</label>
                    <select value={wDirection} onChange={(e) => setWDirection(e.target.value)}
                            className="input-editorial w-56" data-testid="par-wallet-direction">
                      <option value="to_couple">{t("couple.to_couple")}</option>
                      <option value="from_couple">{t("couple.from_couple")}</option>
                    </select>
                  </div>
                  <button type="submit" disabled={wBusy} className="btn-magenta" data-testid="par-wallet-submit">
                    <ArrowUpDown className="h-3.5 w-3.5 inline mr-2" /> {wBusy ? t("couple.transferring") : t("couple.transfer")}
                  </button>
                </div>
                <p className="font-manrope text-[10px] text-[var(--cream-faint)] uppercase tracking-[0.15em]">
                  {t("couple.wallet_equal_access_note")}
                </p>
              </form>
            )}

            <div className="border-t border-white/10 pt-6 mt-6 space-y-3">
              <Link to={`/par/${couple.handle}`} className="btn-ghost inline-block mr-2" data-testid="par-view-public">
                {t("couple.see_public_profile")}
              </Link>
              <Link to="/par-innboks" className="btn-ghost inline-block mr-2" data-testid="par-inbox-link">
                {t("couple.shared_inbox")}
              </Link>
              <button
                onClick={leave}
                disabled={submitting}
                className="text-red-400 text-xs font-meta tracking-[0.15em] hover:text-red-300 ml-2"
                data-testid="par-leave-btn"
              >
                <LogOut className="h-3 w-3 inline mr-1" /> {t("couple.leave_couple")}
              </button>
            </div>
          </div>
        )}

        {!loading && couple && couple.status === "archived" && (
          <div className="panel-tight p-6 border-l-2 border-amber-500">
            <div className="flex items-center gap-2 mb-3 font-meta text-amber-300">
              <Archive className="h-3.5 w-3.5" /> {t("couple.archived")}
            </div>
            <div className="font-fraunces italic text-2xl text-[var(--cream)] mb-2">{couple.name}</div>
            <p className="text-sm text-[var(--cream-soft)]">
              {t("couple.archived_note")}
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
